import React, { Fragment, useCallback, useContext, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _compact from 'lodash/compact';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _capitalize from 'lodash/capitalize';
import { makeStyles } from '@mui/styles';
import { Button, Divider, Grid } from '@mui/material';

import { getFullName, getRubles, toastify } from 'utils/helpers';
import MGrid from 'components/MaterialUI/MGrid';
import MPaper from 'components/MaterialUI/MPaper';
import * as ENDPOINT from 'config/endpoint';
import { appStoreMakeRequest } from 'reactStore/slices/appStoreSlice';
import { OrdersContext } from 'pages/Orders/OrdersList';
import MText from 'components/MaterialUI/MText';
import FormInputDisplay from 'components/FormElements/FormInputDisplay';

import {
  changeCardTypeForCyrillic,
  changeCarPlaceTypeForCyrillic,
  changeRailwayOrderStateForCyrillic,
  changeTicketStateForCyrillic,
  changeTransitDocumentStateForCyrillic,
  changeTicketBlankStateForCyrillic,
} from 'pages/Railway/containers/helpers';
import MToggleIconButton from 'components/MaterialUI/MToggleIconButton';
import DocumentOptions from 'pages/Orders/RailwayOrders/RailwayOrderPassengers/DocumentOptions';
import { getUserDataSelector } from 'reactStore/selectors/userSelector';

const useStyles = makeStyles((theme) => ({
  paper: {
    border: `1px solid ${theme.palette.grey5.main}`,
  },
  paperSelected: {
    zIndex: 2,
    position: 'relative',
    backgroundColor: theme.palette.blue5.main,
    border: `1px solid ${theme.palette.info.main}`,
  },
  index: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey5.main,
    borderRadius: '50%',
    color: theme.palette.white.main,
  },
  indexOpen: {
    backgroundColor: theme.palette.info.main,
  },
  passenger: {
    fontWeight: 500,
  },
  waysDivider: {
    width: '100%',
    height: '6px',
    borderRadius: 0,
    margin: '12px 0',
  },
  state: {
    color: theme.palette.info.main,
  },
}));

const OrderPassengerCard = ({ index, passengerItems, fromCode, toCode, orderId, orderState, isLoading, ownerId }) => {
  const { storeName } = useContext(OrdersContext);
  const user = useSelector(getUserDataSelector, shallowEqual);

  const mapper = useCallback(
    (res, old) => old.map((i) => (i['id'] !== orderId ? i : { ...i, ...res.data.data })),
    [orderId],
  );
  const dispatch = useDispatch();
  const itemFrom = passengerItems.find(
    (item) => item?.reservation?.receipt?.trip_info?.origin_station_code === fromCode,
  );
  const itemTo = passengerItems.find((item) => item?.reservation?.receipt?.trip_info?.origin_station_code === toCode);
  const { traveller, reservation, refund_request } = itemFrom;
  const reverseDate = traveller?.date_of_birth.split('-').reverse().join('-') || '';

  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isDisabledActualizeTransitDocumentsButton, setIsDisabledActualizeTransitDocumentsButton] = useState(false);

  const getNames = () => {
    return getFullName({
      first_name: traveller?.['first_name'],
      last_name: traveller?.['last_name'],
      middle_name: traveller?.['middle_name'],
    });
  };

  const refundRequest = (id) => async () => {
    const dataSource = `${ENDPOINT.REFUND_REQUESTS}`;
    const data = { data: { order_item_id: id, ['refund_type']: 'voluntary' } };
    const resp = await dispatch(appStoreMakeRequest({ dataSource, data, method: 'POST' }));

    if (!resp?.payload?.error) {
      toastify('success', 'Заявка на возврат поступила в обработку');
    } else {
      const errorMessage = resp.payload.error.messages?.join(' ');
      toastify('error', errorMessage);
    }
  };

  const checkTransitDocumentStatus = (orderId) => async () => {
    setIsDisabledActualizeTransitDocumentsButton(true);
    const dataSource = `${ENDPOINT.ORDERS}/${orderId}/actualize_transit_documents`;
    await dispatch(appStoreMakeRequest({ storeName, dataSource, mapper, method: 'PATCH' }));
    setIsDisabledActualizeTransitDocumentsButton(false);
  };

  const wayInfo = _compact([itemFrom, itemTo]);

  const getBookingStatus = () => {
    const hasRefundRequest = !!refund_request?.['state'];
    const blankIncludesJournaledStatus = [
      'electronic_registration_absent',
      'electronic_registration_present',
      'voucher_issued',
      'trip_was_interrupted_and_resumed_after',
    ].includes(reservation?.['receipt']?.['state']);

    const state = reservation?.['state'];

    if (state === 'confirmation_pending') return 'Ожидает выписки';
    if (state === 'unconfirmed') return 'Отменен';

    if (
      (hasRefundRequest && blankIncludesJournaledStatus) ||
      state === 'booked' ||
      (state === 'cancelled' && orderState === 'cancelled')
    ) {
      return changeRailwayOrderStateForCyrillic(state);
    }
    return changeTicketBlankStateForCyrillic(reservation['receipt']?.['state']);
  };

  return (
    <MGrid size={12} columnSpacing={8} alignItems="center">
      <Grid item children={<div className={clsx(classes.index, { [classes.indexOpen]: open })} children={index} />} />
      <Grid item xs>
        <MPaper
          clean
          withGrid
          gridProps={{ rowSpacing: 3 }}
          className={clsx(classes.paper, { [classes.paperSelected]: open })}
        >
          <MGrid size columnSpacing={6} alignItems="center">
            <Grid
              item
              xs={3}
              children={<MText {...{ isLoading }} variant="body2" weight="bold" children={getNames()} />}
            />
            <MGrid size spacing={3}>
              <FormInputDisplay {...{ isLoading }} label={'Статус брони'} value={getBookingStatus() || '-'} />
              <FormInputDisplay {...{ isLoading }} label={t('Labels.DateOfBirth')} value={reverseDate} />
              <FormInputDisplay {...{ isLoading }} label={t('Form.Gender.Label')} value={traveller?.gender} />
              <FormInputDisplay
                {...{ isLoading }}
                label={t('Labels.Nationality')}
                value={_capitalize(traveller?.nationality?.text)}
              />
              <FormInputDisplay
                {...{ isLoading }}
                label={t('Labels.DocumentNumber')}
                value={traveller?.document?.number}
              />
              <FormInputDisplay {...{ isLoading }} label={t('Labels.Email')} value={traveller?.email} />
              <FormInputDisplay {...{ isLoading }} label={t('Labels.Phone')} value={traveller?.phone} />
              {traveller?.transit_document?.state && orderState === 'confirmed' && (
                <FormInputDisplay
                  {...{ isLoading }}
                  label={t('Labels.TransitDocument')}
                  value={changeTransitDocumentStateForCyrillic(traveller?.transit_document?.state)}
                />
              )}
              {(traveller?.transit_document?.state === 'requested' ||
                traveller?.transit_document?.state === 'pending') &&
                orderState === 'confirmed' &&
                wayInfo.some((item) => item.reservation.state === 'confirmed') &&
                !isLoading && (
                  <Grid item size={2}>
                    <Button
                      size="small"
                      color="primary"
                      variant={'contained'}
                      onClick={checkTransitDocumentStatus(orderId)}
                      disabled={isDisabledActualizeTransitDocumentsButton}
                    >
                      {t('Orders.UpdateInfo')}
                    </Button>
                  </Grid>
                )}

              {traveller?.bonus_cards?.map((card, index) => (
                <FormInputDisplay
                  {...{ isLoading }}
                  key={index}
                  label={changeCardTypeForCyrillic(card.card_type)}
                  value={card.card_number}
                />
              ))}
            </MGrid>
          </MGrid>

          <MGrid columnSpacing={2} alignItems="center">
            <Grid item children={<MToggleIconButton {...{ open, setOpen }} />} />
          </MGrid>

          {open &&
            wayInfo?.map((item, index, arr) => {
              const totalOrderAmount = item?.reservation?.actual_sell_price?.order_item_total?.gross?.cents || 0;
              const originalPrice = item?.reservation?.initial_sell_price?.provider_total?.gross?.cents || 0;
              const serviceFee = item?.reservation?.actual_sell_price?.client_fees_total?.gross?.cents || 0;
              const tariffInfo = item?.reservation?.receipt?.tariff_info;
              const way = item?.reservation?.raw_data?.booking_data?.reservation_items?.find(
                (reservation) =>
                  reservation.origin_station_code === item?.reservation?.receipt?.trip_info?.origin_station_code &&
                  reservation.destination_station_code ===
                    item?.reservation?.receipt?.trip_info?.destination_station_code &&
                  reservation?.passengers?.find((passenger) => getFullName(passenger) === getNames()),
              );
              const arrivalDate = (
                <MGrid direction={'column'}>
                  <MText variant={'subtitle1'} weight="bold" children={way?.destination_station} />
                  <MText
                    variant={'subtitle1'}
                    weight="bold"
                    children={moment(way?.['local_arrival_date_time']).format('DD.MM.YYYY' + ' в HH:mm')}
                  />
                </MGrid>
              );
              const departureDate = (
                <MGrid direction={'column'}>
                  <MText variant={'subtitle1'} weight="bold" children={way?.origin_station} />
                  <MText
                    variant={'subtitle1'}
                    weight="bold"
                    children={moment(way?.['local_departure_date_time']).format('DD.MM.YYYY' + ' в HH:mm')}
                  />
                </MGrid>
              );
              const isPersonEqualTraveller =
                (item?.traveller?.person_id === ownerId && user?.role === 'self_booker') || user?.role === 'manager';

              return (
                <Fragment key={index}>
                  <MGrid size={12} rowSpacing={3}>
                    <MGrid size={12} spacing={3}>
                      <FormInputDisplay
                        {...{ isLoading }}
                        size={3}
                        label={'Номер билета'}
                        value={reservation['receipt']?.['number']}
                      />
                      <FormInputDisplay
                        {...{ isLoading }}
                        size={3}
                        label={`${t('Orders.Departure')} ${t('Orders.LocalTime')}`}
                        value={departureDate}
                      />
                      <FormInputDisplay
                        {...{ isLoading }}
                        size={3}
                        label={`${t('Orders.Arrival')} ${t('Orders.LocalTime')}`}
                        value={arrivalDate}
                      />
                    </MGrid>

                    <MGrid size={12} spacing={3}>
                      <FormInputDisplay
                        {...{ isLoading }}
                        label={t('Orders.Tariff')}
                        value={getRubles(originalPrice)}
                      />
                      <FormInputDisplay
                        {...{ isLoading }}
                        label={t('Orders.ServiceFee')}
                        value={getRubles(serviceFee)}
                      />
                      <FormInputDisplay {...{ isLoading }} label={t('Orders.TotalToRefund')} value={'-'} />
                      <FormInputDisplay
                        {...{ isLoading }}
                        label={t('Orders.TotalCost')}
                        value={getRubles(totalOrderAmount)}
                      />
                    </MGrid>

                    <MGrid size={8} spacing={3}>
                      <Grid item xs={2}>
                        <MText
                          {...{ isLoading }}
                          variant="subtitle1"
                          color="textSecondary"
                          children={'Статус билета'}
                        />
                        <MText
                          {...{ isLoading }}
                          variant="subtitle1"
                          weight="bold"
                          children={changeTicketStateForCyrillic(reservation['receipt']?.['state']) || '-'}
                        />
                      </Grid>
                      <FormInputDisplay {...{ isLoading }} label={`${t('Orders.Train')}`} value={way?.train_number} />
                      <FormInputDisplay
                        {...{ isLoading }}
                        label={t('Orders.PlaceType')}
                        value={changeCarPlaceTypeForCyrillic(way?.place_reservation_type)}
                      />
                      <FormInputDisplay
                        {...{ isLoading }}
                        label={t('Orders.PlaceNumber')}
                        value={item?.reservation?.receipt?.trip_info?.seat_numbers?.join(', ')}
                      />
                      <FormInputDisplay
                        {...{ isLoading }}
                        size={2}
                        label={t('Orders.ServiceClass')}
                        value={way?.['service_class']}
                      />
                      {!['Senior', 'Junior', 'Child17', 'Child', 'FreeChild'].includes(tariffInfo?.type) && (
                        <FormInputDisplay
                          {...{ isLoading }}
                          label={t('Orders.TariffName')}
                          value={_capitalize(tariffInfo?.['display_name'])}
                        />
                      )}
                    </MGrid>

                    <MGrid size={4} spacing={3} justifyContent="flex-end">
                      {item?.reservation?.state === 'confirmed' && isPersonEqualTraveller && (
                        <Grid item size={2}>
                          <Button size="small" variant="contained" color="primary" onClick={refundRequest(item.id)}>
                            {t('Orders.TicketRefund')}
                          </Button>
                        </Grid>
                      )}
                      {!isLoading && (
                        <Grid item size={3}>
                          <DocumentOptions {...{ orderItem: item }} />
                        </Grid>
                      )}
                    </MGrid>
                  </MGrid>

                  {index !== arr.length - 1 && <Divider />}
                </Fragment>
              );
            })}
        </MPaper>
      </Grid>
    </MGrid>
  );
};

OrderPassengerCard.propTypes = {
  fromCode: PropTypes.string,
  toCode: PropTypes.string,
  orderId: PropTypes.string,
  orderState: PropTypes.string,
  index: PropTypes.number,
  isLoading: PropTypes.bool,
  passengerItems: PropTypes.array,
  orderItem: PropTypes.object,
  ownerId: PropTypes.string,
};

OrderPassengerCard.defaultProps = {
  orderItem: {},
};

export default OrderPassengerCard;
